import React, { useState, useEffect, Profiler } from 'react';
import { Heart, Share2, Trash2, Copy, Pencil, RefreshCcw } from 'lucide-react';
import { useBilling } from '../../hooks/useBilling';
import './screens.css';

const AppListScreen = ({
  lineId,
  userName,
  apps,
  deleteApp,
  goToHome,
  handleShare,
  viewApp,
  handleLike,
  handleRemoveLike, // いいね取り消し処理を追加
  onEditPublicName,
  onRefresh,
}) => {
  // 課金情報を取得
  const { billingData, loading: billingLoading } = useBilling(lineId);

  // いいね済みアプリのIDを保存する状態
  const [likedAppIds, setLikedAppIds] = useState([]);
  
  // コンポーネントマウント時にローカルストレージからいいね済みリストを取得
  useEffect(() => {
    const likedApps = JSON.parse(localStorage.getItem('likedApps') || '[]');
    setLikedAppIds(likedApps);
  }, []);
  
  // いいねボタンのクリックハンドラ - トグル機能を持たせる
  const onLike = async (e, app) => {
    // イベントの伝播を停止（親要素のクリックを防止）
    e.stopPropagation();
    
    try {
      if (likedAppIds.includes(app.id)) {
        // いいねを取り消す
        const newLikes = await handleRemoveLike(app.id);
        
        if (newLikes !== null) {
          // ローカルストレージからいいねを削除
          const updatedLikedApps = likedAppIds.filter(id => id !== app.id);
          setLikedAppIds(updatedLikedApps);
          localStorage.setItem('likedApps', JSON.stringify(updatedLikedApps));
          
          // 表示も更新
          app.likes = newLikes;
        }
      } else {
        // いいねを追加
        const newLikes = await handleLike(app.id);
        
        if (newLikes !== null) {
          // いいね済みリストに追加
          const newLikedAppIds = [...likedAppIds, app.id];
          setLikedAppIds(newLikedAppIds);
          localStorage.setItem('likedApps', JSON.stringify(newLikedAppIds));
          
          // 表示も更新
          app.likes = newLikes;
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // レシピボタンのクリックハンドラ
  const copyRecipe = async (e, app) => {
    e.stopPropagation();
    
    if (!app.spec) {
      alert('レシピが見つかりません');
      return;
    }
    
    // クリップボードにコピー
    try {
      await navigator.clipboard.writeText(app.spec);
      // アラート表示
      alert(`レシピをコピーしました！\n\n「${app.spec}」`);
    } catch (err) {
      // navigator.clipboardが使えない場合はアラートのみ表示
      alert(`レシピをコピーできませんでした。\n\n「${app.spec}」`);
    }
  };

  // 有効期限を計算する関数
  const calculateExpiryDate = (app) => {
    // トライアルプランでない場合は表示しない
    if (!billingData || billingData.plan !== 'trial') {
      return null;
    }

    // billingStartDateがない場合は課金システム導入前のアプリなので表示しない
    if (!app.billingStartDate) {
      return null;
    }

    // テスト環境かどうか判定
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'miniapplify';
    const isTestProject = /test/i.test(projectId);

    // billingStartDateから有効期限を計算
    let billingStartDate;

    // Firestoreのタイムスタンプオブジェクトの場合
    if (app.billingStartDate.toDate && typeof app.billingStartDate.toDate === 'function') {
      billingStartDate = app.billingStartDate.toDate();
    }
    // Timestamp形式（seconds, nanoseconds）の場合
    else if (app.billingStartDate.seconds) {
      billingStartDate = new Date(app.billingStartDate.seconds * 1000);
    }
    // 文字列やその他の形式の場合
    else {
      billingStartDate = new Date(app.billingStartDate);
    }

    // デバッグログ
    console.log('App billingStartDate debug:', {
      appId: app.id,
      raw: app.billingStartDate,
      parsed: billingStartDate,
      isValid: billingStartDate instanceof Date && !isNaN(billingStartDate)
    });

    // 無効な日付の場合は null を返す
    if (isNaN(billingStartDate.getTime())) {
      console.warn('Invalid billingStartDate for app:', app.id, app.billingStartDate);
      return null;
    }

    const expiresAt = new Date(billingStartDate);

    if (isTestProject) {
      // テスト環境: 30分後に期限切れ
      expiresAt.setMinutes(expiresAt.getMinutes() + 30);
    } else {
      // 本番環境: 7日後の23:59:59に期限切れ
      expiresAt.setDate(expiresAt.getDate() + 7);
      expiresAt.setHours(23, 59, 59, 999);
    }

    return expiresAt;
  };

  // 有効期限を日本語形式でフォーマット
  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return null;

    // テスト環境かどうか判定（projectIdに'test'が含まれるか）
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'miniapplify';
    const isTestProject = /test/i.test(projectId);

    const month = expiryDate.getMonth() + 1;
    const day = expiryDate.getDate();

    if (isTestProject) {
      // テスト環境: 時刻も表示
      const hours = expiryDate.getHours();
      const minutes = expiryDate.getMinutes();
      return `${month}月${day}日 ${hours}:${minutes.toString().padStart(2, '0')}まで有効`;
    } else {
      // 本番環境: 日付のみ
      return `${month}月${day}日まで有効`;
    }
  };

  // アプリが期限切れかどうか判定
  const isAppExpired = (app) => {
    const expiryDate = calculateExpiryDate(app);
    if (!expiryDate) return false; // 有効期限がない場合は期限切れではない

    const now = new Date();
    return now > expiryDate;
  };

  // 画面内更新ボタンで明示的に再読み込みします（LINE内のプル更新は無効化）

  // 完成したアプリのみフィルタリング
  const completedApps = apps.filter(app => app.content && app.content.trim() !== '');

  return (
    <div className="Applist app-list-container">
      {/* タイトル行（中央タイトル＋右端に更新ボタン、左はダミースペーサーで中央を保つ） */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px' }}>
        <div style={{ width: 32, height: 32 }} />
        <h2
          className="Applist_Title app-list-title"
          style={{ textAlign: 'center', margin: 0, flex: '0 1 auto' }}
        >
          <span>アプリ一覧</span>
        </h2>
        {onRefresh ? (
          <button
            className="action-button refresh-button"
            title="更新"
            onClick={(e) => { e.preventDefault(); onRefresh(); }}
            aria-label="一覧を更新"
            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <RefreshCcw size={18} />
          </button>
        ) : (
          <div style={{ width: 32, height: 32 }} />
        )}
      </div>
      
      {onEditPublicName && (
        <div className="public-name-container">
          <span className="public-name-text">公開名: </span>
          <button
            className="public-name-edit-button"
            title="公開表示名を編集"
            onClick={(e) => {
              e.preventDefault();
              onEditPublicName();
            }}
          >
            {userName} <Pencil size={16} />
          </button>
        </div>
      )}
      
      {completedApps.length > 0 ? (
        <div className="Applist_Items app-list-items">
          {completedApps.map(app => {
            const expired = isAppExpired(app);
            return (
            <div
              key={app.id}
              className={`Applist_Item app-list-item ${
                app.shared === "true" ? 'Applist_Item--shared' : 'Applist_Item--personal'
              } ${expired ? 'app-list-item--expired' : ''}`}
              onClick={expired ? undefined : () => viewApp(app)}
              style={expired ? { cursor: 'not-allowed' } : undefined}
            >
              <div className="Applist_ItemIcon app-item-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 126 130"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M62.7143 0.00239304C88.5247 -0.165448 111.092 14.5566 118.885 36.6479C122.85 47.8875 121.887 58.8553 115.927 70.3129C110.785 80.1993 100.67 90.1415 90.6059 95.2016C85.4056 97.8161 76.6434 100.276 70.9955 100.707C69.7402 100.804 69.7551 100.818 71.7279 101.444C77.797 103.366 84.545 103.597 90.0277 102.069C99.7131 99.3684 109.89 91.6103 116.226 82.0981C119.739 76.8243 123.325 69.0084 125.116 62.7231L125.842 60.1723L125.869 63.7543C125.9 68.1564 125.352 73.3336 124.371 77.8969C122.138 88.2893 116.591 99.352 109.633 107.288C98.7802 119.666 85.8137 126.948 70.2641 129.397C65.6271 130.127 55.3056 130.213 51.0492 129.556C41.7732 128.126 33.3608 124.921 26.14 120.069C15.614 112.995 7.96365 103.296 3.51503 91.3852C1.10761 84.9392 0.0110463 78.6405 0.000383547 71.1987C-0.0157199 59.7001 3.04975 49.9667 9.30702 41.6489C16.3553 32.2801 27.3962 25.4967 36.974 24.6499L39.3383 24.4409L36.3178 26.062C32.252 28.2436 28.5971 31.1033 24.6605 35.184C14.4554 45.7633 8.91279 61.2644 10.2533 75.479C11.5885 89.6365 16.9699 100.442 27.6752 110.457C32.7437 115.2 39.1781 119.061 45.7152 121.284C49.739 122.651 50.3692 122.425 47.2582 120.729C39.0218 116.239 31.3538 107.574 26.6771 97.4702C25.2374 94.359 23.199 88.2673 23.4975 87.9682C23.5938 87.8721 24.301 88.7426 25.0697 89.9028C30.3549 97.8818 38.2014 104.922 46.2914 108.944C58.5329 115.029 73.6185 115.697 86.7943 110.738C90.4417 109.365 96.8834 105.891 96.5023 105.502C96.4155 105.417 94.9257 105.759 93.1918 106.261C87.8142 107.819 82.5085 108.425 76.4857 108.169C65.103 107.686 54.6277 104.409 46.1244 98.6713C42.8222 96.4432 37.5593 91.4435 35.0756 88.1752C32.572 84.8807 29.8267 79.1543 28.6635 74.8032C27.7869 71.524 27.6859 70.498 27.6713 64.7465C27.6614 60.7772 27.8357 57.5309 28.1303 56.2084C28.3911 55.037 28.5306 54.0044 28.4418 53.9116C28.1831 53.6571 25.4151 57.3962 23.4848 60.6079C21.9403 63.1783 19.6392 69.2317 18.7807 72.9819C18.3072 75.051 17.9937 70.7408 18.3139 66.5522C18.794 60.2704 20.5812 54.2989 23.4809 49.2885C31.9004 34.7397 46.9996 25.9952 62.2562 26.8315C64.5948 26.96 67.4444 27.309 68.5873 27.6069C71.601 28.3917 71.6812 28.3202 69.8822 26.4663C66.2888 22.7639 60.3523 19.0296 55.9906 17.728C47.5184 15.2001 38.483 15.5944 29.6547 18.8784C24.3142 20.865 14.3144 25.9738 11.3822 28.2133C9.67636 29.5162 9.86537 28.8363 12.1381 25.4946C14.9921 21.2981 21.4076 14.8282 25.7357 11.7817C29.3774 9.21793 36.8953 5.29032 40.9076 3.85493C44.4102 2.60182 50.2534 1.13237 54.1488 0.524854C55.8667 0.257364 59.7209 0.0217922 62.7143 0.00239304ZM56.7123 50.4018L40.3402 49.2875L46.9516 63.5522L40.3021 82.3403L59.1361 78.6684L74.3803 90.3217L75.4545 70.227L89.0228 60.6889L72.2484 55.392L69.1986 35.7006L56.7123 50.4018ZM69.8666 5.24849C66.9163 4.60767 54.9 4.10627 56.1732 4.6772C56.577 4.85783 58.4708 5.35869 60.3822 5.79146C77.9316 9.76484 93.5054 27.8104 97.0014 48.2221C99.1397 60.7083 96.2662 74.2869 89.642 82.9926C88.8222 84.0701 88.2148 85.0162 88.2924 85.0942C88.6395 85.4356 92.3582 83.205 94.6508 81.2797C106.076 71.6836 110.877 52.6807 105.89 36.8002C100.914 20.9555 87.3063 9.03765 69.8666 5.24849Z"
                    fill="#FFD300"
                  />
                </svg>
              </div>
              <div className="Applist_ItemContent app-item-content">
                <h3 className="Applist_ItemTitle app-item-title">{app.title}</h3>
                {app.created_at && (
                  <div className="Applist_DateInfo app-date-info">
                    {new Date(app.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                    {(() => {
                      const expiryDate = calculateExpiryDate(app);
                      const expiryText = formatExpiryDate(expiryDate);
                      return expiryText ? (
                        <span className="Applist_ExpiryDate app-expiry-date"> | {expiryText}</span>
                      ) : null;
                    })()}
                  </div>
                )}
                <div className="Applist_ItemActions app-item-actions">
                  <div className="app-item-badge-wrapper">
                    {/* 期限切れバッジ */}
                    {expired && (
                      <span className="Applist_Badge Applist_Badge--expired app-badge expired-badge">
                        <span className="Applist_BadgeDot badge-dot">●</span> 期限切れ
                      </span>
                    )}
                    {/* データタイプバッジ */}
                    {!expired && (app.shared === "true" ? (
                      <span className="Applist_Badge Applist_Badge--shared app-badge shared-badge">
                        <span className="Applist_BadgeDot badge-dot">●</span> 共有データ
                      </span>
                    ) : (
                      <span className="Applist_Badge Applist_Badge--personal app-badge personal-badge">
                        <span className="Applist_BadgeDot badge-dot">●</span> 個人データ
                      </span>
                    ))}
                  </div>
                  <div className="app-item-buttons">
                    {/* レシピボタン */}
                    <button
                      className="Applist_ActionButton Applist_CopyButton action-button copy-button"
                      onClick={(e) => copyRecipe(e, app)}
                      title="レシピをコピー"
                    >
                      <Copy size={16} />
                    </button>

                    {/* いいねボタン */}
                    <button
                      className="Applist_ActionButton Applist_LikeButton action-button like-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLike(e, app);
                      }}
                      title={likedAppIds.includes(app.id) ? 'いいねを取り消す' : 'いいね！'}
                    >
                      <Heart 
                        size={16} 
                        className={`${likedAppIds.includes(app.id) ? 'heart-icon-filled' : 'heart-icon'}`} 
                      />
                      <span className="Applist_LikeCount like-count">{app.likes || 0}</span>
                    </button>
                                    
                    {/* シェアボタン */}
                    <button
                      className="Applist_ActionButton Applist_ShareButton action-button share-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare(app);
                      }}
                      title="シェアする"
                    >
                      <Share2 size={16} />
                    </button>
                    
                    {/* 削除ボタン */}
                    <button
                      className="Applist_ActionButton Applist_DeleteButton action-button delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteApp(app.id);
                      }}
                      title="削除する"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="Applist_EmptyMessage empty-message">
          <p>作成したアプリはありません</p>
        </div>
      )}
      
    </div>
  );
};

export default AppListScreen;
